import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { QuadStarIcon, Typography } from '@ag1/ui';
import { LocaleEnum } from '@portal/schema/locale';
import { useStoreValue } from '@portal/state/hooks/useStore';
import { useMoney } from '@portal/shared/hooks/useMoney';
import { useLocale } from '@portal/shared/hooks/useLocale';
import { getShopifyVariantId } from '@portal/shared/utils/subscriptionV2Helpers';
import { isAGZDiscountedVariant } from '@portal/shared/utils/shopify/isAGZDiscountedVariant';
import { isAGZSubscriptionVariant } from '@portal/shared/utils/shopify/isAGZSubscriptionVariant';
import { isOmegaBundleVariant } from '@portal/shared/utils/shopify/isOmegaBundleVariant';
import { isOmegaSubscriptionVariant } from '@portal/shared/utils/shopify/isOmegaSubscriptionVariant';
import {
  OMEGA_SUBSCRIBE_AND_SAVE_BUNDLE_PRICE,
  OMEGA_SUBSCRIBE_AND_SAVE_PRICE,
  AGZ_OTP_SUBSCRIBE_AND_SAVE_BUNDLE_PRICE,
  AGZ_OTP_SUBSCRIBE_AND_SAVE_PRICE
} from '../SiteCart/constants';
import { styles } from './BundleSavings.css';

interface BundleSavingsProps {
  itemId: string;
  itemQuantity: number;
  currencyCode: string;
  isSubscription?: boolean;
}

export const BundleSavings = ({
  itemId,
  itemQuantity,
  currencyCode,
  isSubscription = false
}: BundleSavingsProps) => {
  const store = useStoreValue();
  const { formatMoney } = useMoney();
  const { t } = useTranslation('siteCart');
  const locale = useLocale();
  const isUsLocale = locale === LocaleEnum.enUS;

  // Detect Omega products and calculate savings
  const detectOmegaSavings = useCallback(() => {
    if (!isSubscription) return null;

    const variantId = getShopifyVariantId(itemId);

    // Check if this is the bundle variant (bundlePrice) vs regular subscription (subscriptionPrice)
    const isSubscriptionVariant = isOmegaSubscriptionVariant(variantId, store);
    const isBundleVariant = isOmegaBundleVariant(variantId, store);

    let savings = 0;

    if (isSubscriptionVariant) {
      savings = OMEGA_SUBSCRIBE_AND_SAVE_PRICE;
    }

    if (isBundleVariant) {
      savings = OMEGA_SUBSCRIBE_AND_SAVE_BUNDLE_PRICE;
    }

    // Only show discount message if there's actually a discount pricing
    if (savings <= 0) return null;

    const totalSavings = savings * itemQuantity;

    return {
      amount: totalSavings,
      text: t(isBundleVariant ? 'omegaBundleSavings' : 'omegaSubscriptionSavings', {
        amount: formatMoney(totalSavings, currencyCode, { hideDecimalsForWholeNumbers: true })
      })
    };
  }, [itemId, itemQuantity, currencyCode, isSubscription, store, formatMoney, t]);

  const detectAgzSavings = useCallback(() => {
    if (!isSubscription) return null;

    const variantId = getShopifyVariantId(itemId);

    // Check if this is the bundle variant (bundlePrice) vs regular subscription (subscriptionPrice)
    const isSubscriptionVariant = isAGZSubscriptionVariant(variantId, store);
    const isBundleVariant = isAGZDiscountedVariant(variantId, store);

    let savings = 0;

    if (isSubscriptionVariant) {
      savings = AGZ_OTP_SUBSCRIBE_AND_SAVE_PRICE;
    }

    if (isBundleVariant) {
      savings = AGZ_OTP_SUBSCRIBE_AND_SAVE_BUNDLE_PRICE;
    }

    // Only show discount message if there's actually a discount pricing
    if (savings <= 0) return null;

    const totalSavings = savings * itemQuantity;

    return {
      amount: totalSavings,
      text: t(isBundleVariant ? 'agzBundleSavings' : 'agzSubscriptionSavings', {
        amount: formatMoney(totalSavings, currencyCode, { hideDecimalsForWholeNumbers: true })
      })
    };
  }, [itemId, itemQuantity, currencyCode, store, formatMoney, t, isSubscription]);

  // Prioritize omega savings over AGZ savings
  const savings = detectOmegaSavings() || detectAgzSavings();

  if (!isUsLocale || !savings) return null;

  return (
    <div className={styles.savingsContainer}>
      <QuadStarIcon className={styles.savingsIcon} />
      <Typography variant="portalCaption" size="1" noPadding>
        {savings?.text}
      </Typography>
    </div>
  );
};
